/* eslint-disable max-statements */
import {
	BufferAttribute,
	BufferGeometry,
	ConeGeometry,
	LineBasicMaterial,
	LineSegments,
	Mesh,
	MeshStandardMaterial,
	Quaternion,
	Scene,
	SphereGeometry,
	SpotLight,
	Vector3
} from 'three';

import {
	getQualitySegments,
	LIGHT_HELPER_DEFAULTS,
	RenderQuality,
	SpotLightHelperObjects
} from './light-helper.types';

/**
 * Creates visualization objects for a SpotLight (cone + sphere + line)
 *
 * @param light The SpotLight to visualize
 * @param scene Scene to add objects to
 * @param coneRadius Radius of the cone base
 * @param coneHeight Height of the cone
 * @param visible Initial visibility state
 * @param quality Render quality 0-10
 * @returns SpotLight helper objects
 */
export const createSpotLightVisualization = (
	light: SpotLight,
	scene: Scene,
	coneRadius: number = LIGHT_HELPER_DEFAULTS.CONE_RADIUS,
	coneHeight: number = LIGHT_HELPER_DEFAULTS.CONE_HEIGHT,
	visible = false,
	quality: RenderQuality = LIGHT_HELPER_DEFAULTS.QUALITY
): SpotLightHelperObjects => {
	const isLightOn = light.visible && light.intensity > 0;
	const lightColor = light.color.getHex();
	const segments = getQualitySegments(quality);

	// Reduce cone height by 50%
	const adjustedConeHeight = coneHeight * 0.5;

	// Calculate cone radius based on spotlight angle
	// SpotLight.angle is the angle from center to edge (half-angle) in radians
	// radius = height * tan(angle)
	const angleBasedRadius = adjustedConeHeight * Math.tan(light.angle);

	// Calculate direction from light to target (cone points toward target)
	const direction = new Vector3()
		.subVectors(light.target.position, light.position)
		.normalize();

	// Create cone (inverted - tip at light position, base toward target)
	const coneColor = isLightOn ? lightColor : LIGHT_HELPER_DEFAULTS.CONE_COLOR_OFF;
	const coneGeometry = new ConeGeometry(angleBasedRadius, adjustedConeHeight, segments.radial, segments.height);
	const coneMaterial = new MeshStandardMaterial({ color: coneColor, wireframe: false }); // Interface objects never wireframe
	const cone = new Mesh(coneGeometry, coneMaterial);
	cone.name = `spotLightCone_${light.uuid}`;
	// Mark as interface object to exclude from wireframe mode
	cone.userData.isInterface = true;
	cone.userData.type = 'helper';

	// Position cone so tip is at light position (offset by half cone height in direction)
	const coneOffset = direction.clone().multiplyScalar(adjustedConeHeight * 0.5);
	cone.position.copy(light.position).add(coneOffset);

	// Rotate cone so tip points toward light position (base opens toward target)
	const up = new Vector3(0, 1, 0); // Cone tip points up by default
	const quaternion = new Quaternion();
	quaternion.setFromUnitVectors(up, direction.clone().negate()); // Negate to point tip back toward light
	cone.setRotationFromQuaternion(quaternion);
	cone.visible = visible;
	scene.add(cone);

	// Create sphere at light position (center of cone tip)
	// Sphere size based on cone radius for visual consistency
	const sphereRadius = Math.max(coneRadius * 0.5, angleBasedRadius * 0.3);
	const sphereGeometry = new SphereGeometry(sphereRadius, segments.radial, segments.radial);
	const sphereColor = isLightOn
		? LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_ON
		: LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF;
	const sphereMaterial = new MeshStandardMaterial({ color: sphereColor, wireframe: false }); // Interface objects never wireframe
	const sphere = new Mesh(sphereGeometry, sphereMaterial);
	sphere.name = `spotLightSphere_${light.uuid}`;
	// Mark as interface object to exclude from wireframe mode
	sphere.userData.isInterface = true;
	sphere.userData.type = 'helper';

	// Sphere is at light position
	sphere.position.copy(light.position);
	sphere.visible = visible;
	scene.add(sphere);

	// Create line from light to target
	const lineGeometry = new BufferGeometry();
	const linePositions = new Float32Array([
		light.position.x,
		light.position.y,
		light.position.z,
		light.target.position.x,
		light.target.position.y,
		light.target.position.z
	]);
	lineGeometry.setAttribute('position', new BufferAttribute(linePositions, 3));
	const lineMaterial = new LineBasicMaterial({ color: lightColor });
	const line = new LineSegments(lineGeometry, lineMaterial);
	line.name = `spotLightLine_${light.uuid}`;
	// Mark as interface object to exclude from wireframe mode
	line.userData.isInterface = true;
	line.userData.type = 'helper';
	line.visible = visible && isLightOn;
	scene.add(line);

	return { cone, sphere, line };
};

/**
 * Updates SpotLight visualization objects
 *
 * Note: Cone geometry (radius based on angle) is set at creation time.
 * If spotlight angle changes, the helper should be recreated.
 */
export const updateSpotLightVisualization = (
	light: SpotLight,
	objects: SpotLightHelperObjects,
	coneHeight: number,
	visible: boolean
): void => {
	const isLightOn = light.visible && light.intensity > 0;
	const lightColor = light.color.getHex();

	// Reduce cone height by 50%
	const adjustedConeHeight = coneHeight * 0.5;

	// Calculate direction from light to target
	const direction = new Vector3()
		.subVectors(light.target.position, light.position)
		.normalize();

	// Update cone position (tip at light position, offset by half height toward target)
	const coneOffset = direction.clone().multiplyScalar(adjustedConeHeight * 0.5);
	objects.cone.position.copy(light.position).add(coneOffset);

	// Update cone rotation (tip points toward light position)
	const up = new Vector3(0, 1, 0);
	const quaternion = new Quaternion();
	quaternion.setFromUnitVectors(up, direction.clone().negate());
	objects.cone.setRotationFromQuaternion(quaternion);
	objects.cone.visible = visible;

	// Update cone color
	const coneMaterial = objects.cone.material as MeshStandardMaterial;
	coneMaterial.color.setHex(isLightOn ? lightColor : LIGHT_HELPER_DEFAULTS.CONE_COLOR_OFF);

	// Update sphere position (at light position)
	objects.sphere.position.copy(light.position);
	objects.sphere.visible = visible;

	// Update sphere color
	const sphereMaterial = objects.sphere.material as MeshStandardMaterial;
	sphereMaterial.color.setHex(isLightOn ? LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_ON : LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF);

	// Update line
	const linePositions = objects.line.geometry.attributes.position as BufferAttribute;
	linePositions.setXYZ(0, light.position.x, light.position.y, light.position.z);
	linePositions.setXYZ(1, light.target.position.x, light.target.position.y, light.target.position.z);
	linePositions.needsUpdate = true;
	objects.line.visible = visible && isLightOn;

	// Update line color
	const lineMaterial = objects.line.material as LineBasicMaterial;
	lineMaterial.color.setHex(lightColor);
};

/**
 * Disposes SpotLight visualization objects
 */
export const disposeSpotLightVisualization = (
	objects: SpotLightHelperObjects,
	scene: Scene
): void => {
	scene.remove(objects.cone);
	scene.remove(objects.sphere);
	scene.remove(objects.line);

	objects.cone.geometry.dispose();
	objects.sphere.geometry.dispose();
	objects.line.geometry.dispose();

	(objects.cone.material as MeshStandardMaterial).dispose();
	(objects.sphere.material as MeshStandardMaterial).dispose();
	(objects.line.material as LineBasicMaterial).dispose();
};

